import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

// ─── helpers ────────────────────────────────────────────────────────────────
const FREQ_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually' }
const FREQ_COLORS = { monthly: '#10B981', quarterly: '#3B82F6', annually: '#8B5CF6' }
const STATUS_COLORS = { draft:'#62615D', sent:'#3B82F6', paid:'#10B981', overdue:'#EF4444', cancelled:'#9CA3AF' }
const STATUS_BG    = { draft:'#F7F3EB', sent:'#DBEAFE', paid:'#D1FAE5', overdue:'#FEE2E2', cancelled:'#F3F4F6' }

function nextDate(freq) {
  const d = new Date()
  if (freq === 'monthly')   d.setMonth(d.getMonth() + 1)
  if (freq === 'quarterly') d.setMonth(d.getMonth() + 3)
  if (freq === 'annually')  d.setFullYear(d.getFullYear() + 1)
  return d.toISOString().split('T')[0]
}

function fmt(n) { return `$${parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` }

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}
      onClick={e=>{if(e.target===e.currentTarget)onClose()}}>
      <div className="card animate-fadeUp" style={{width:'100%',maxWidth:wide?720:520,maxHeight:'92vh',overflow:'auto',padding:0}}>
        <div style={{padding:'18px 22px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:'#fff',zIndex:10}}>
          <h2 style={{fontSize:17}}>{title}</h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,color:'#62615D',cursor:'pointer'}}>×</button>
        </div>
        <div style={{padding:22}}>{children}</div>
      </div>
    </div>
  )
}

// ─── main component ──────────────────────────────────────────────────────────
export default function BillingPage() {
  const { profile } = useAuth()
  const location = useLocation()
  const highlightId = location.state?.highlightInvoice || null
  const highlightTimerRef = useRef(null)
  const [tab, setTab] = useState('overview')
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [recurring, setRecurring] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState('')

  // Invoice modal state
  const [showInvModal, setShowInvModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingInv, setViewingInv] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [invForm, setInvForm] = useState({ client_id:'', line_items:[{description:'',qty:1,rate:''}], tax_rate:0, notes:'', due_date:'' })
  const [invGenerating, setInvGenerating] = useState(false)

  // Recurring modal state
  const [showRecModal, setShowRecModal] = useState(false)
  const [editingRec, setEditingRec] = useState(null)
  const [recForm, setRecForm] = useState({ client_id:'', frequency:'monthly', next_invoice_date:nextDate('monthly'), line_items:[{description:'',qty:1,rate:''}], tax_rate:0, notes:'', active:true })
  const [genLoading, setGenLoading] = useState(null)

  // Payment plan modal state
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [planForm, setPlanForm] = useState({ client_id:'', invoice_id:'', total_amount:'', num_installments:3, start_date:new Date().toISOString().split('T')[0] })

  const notify = msg => { setToast(msg); setTimeout(()=>setToast(''),3000) }

  useEffect(()=>{ load() },[])

  useEffect(() => {
    if (!highlightId) return

    setTab('invoices')

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = setTimeout(() => {
      const el = document.getElementById(String(highlightId))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)

    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current)
        highlightTimerRef.current = null
      }
    }
  }, [highlightId])

  async function load() {
    const { data:c } = await supabase.from('clients').select('id,full_name,business_name,email').eq('cpa_id',profile.id).order('business_name')
    setClients(c||[])
    const ids = (c||[]).map(x=>x.id)
    if (ids.length) {
      const [{ data:inv },{ data:rec },{ data:pl }] = await Promise.all([
        supabase.from('invoices').select('*').in('client_id',ids).order('created_at',{ascending:false}),
        supabase.from('recurring_invoices').select('*').eq('cpa_id',profile.id).order('next_invoice_date'),
        supabase.from('payment_plans').select('*').in('client_id',ids).order('created_at',{ascending:false})
      ])
      setInvoices(inv||[])
      setRecurring(rec||[])
      setPlans(pl||[])
    }
    setLoading(false)
  }

  const clientName = id => { const c=clients.find(x=>x.id===id); return c?.business_name||c?.full_name||'—' }

  // ── invoice helpers ──
  const setInvLine = (i,k,v) => setInvForm(f=>({...f,line_items:f.line_items.map((l,j)=>j===i?{...l,[k]:v}:l)}))
  const invTotal = invForm.line_items.reduce((s,l)=>s+(l.qty*parseFloat(l.rate||0)),0)

  async function saveInvoice() {
    if (!invForm.client_id) { notify('Select a client'); return }
    setInvGenerating(true)
    const num = `CF-${Date.now().toString().slice(-6)}`
    const tax = invTotal * ((invForm.tax_rate||0)/100)
    await supabase.from('invoices').insert({
      client_id: invForm.client_id,
      cpa_id: profile.id,
      invoice_number: num,
      line_items: invForm.line_items.filter(l=>l.description),
      tax_rate: invForm.tax_rate,
      total_amount: invTotal + tax,
      status: 'sent',
      notes: invForm.notes,
      due_date: invForm.due_date || null
    })
    notify(`Invoice ${num} created!`)
    setShowInvModal(false)
    setInvForm({ client_id:'', line_items:[{description:'',qty:1,rate:''}], tax_rate:0, notes:'', due_date:'' })
    load()
    setInvGenerating(false)
  }

  async function updateInvoiceStatus(id, status) {
    await supabase.from('invoices').update({ status }).eq('id', id)
    load()
  }

  // ── recurring helpers ──
  const setRecLine = (i,k,v) => setRecForm(f=>({...f,line_items:f.line_items.map((l,j)=>j===i?{...l,[k]:v}:l)}))
  const recTotal = recForm.line_items.reduce((s,l)=>s+(l.qty*parseFloat(l.rate||0)),0)

  function openNewRec() { setEditingRec(null); setRecForm({ client_id:'', frequency:'monthly', next_invoice_date:nextDate('monthly'), line_items:[{description:'',qty:1,rate:''}], tax_rate:0, notes:'', active:true }); setShowRecModal(true) }
  function openEditRec(r) { setEditingRec(r); setRecForm({ client_id:r.client_id, frequency:r.frequency, next_invoice_date:r.next_invoice_date, line_items:r.line_items||[{description:'',qty:1,rate:''}], tax_rate:r.tax_rate||0, notes:r.notes||'', active:r.active }); setShowRecModal(true) }

  async function saveRec() {
    if (!recForm.client_id) { notify('Select a client'); return }
    const payload = { ...recForm, cpa_id: profile.id }
    editingRec ? await supabase.from('recurring_invoices').update(payload).eq('id',editingRec.id)
               : await supabase.from('recurring_invoices').insert(payload)
    notify(editingRec ? 'Updated!' : 'Recurring invoice created!')
    setShowRecModal(false)
    load()
  }

  async function generateNow(r) {
    setGenLoading(r.id)
    const sub = r.line_items.reduce((s,l)=>s+(l.qty*parseFloat(l.rate||0)),0)
    const tax = sub * ((r.tax_rate||0)/100)
    const num = `CF-${Date.now().toString().slice(-6)}`
    await supabase.from('invoices').insert({ client_id:r.client_id, cpa_id:profile.id, invoice_number:num, line_items:r.line_items, tax_rate:r.tax_rate, total_amount:sub+tax, status:'sent', notes:r.notes, due_date:new Date(Date.now()+30*86400000).toISOString().split('T')[0] })
    await supabase.from('recurring_invoices').update({ next_invoice_date:nextDate(r.frequency) }).eq('id',r.id)
    notify(`Invoice ${num} generated for ${clientName(r.client_id)}!`)
    load()
    setGenLoading(null)
  }

  async function toggleRec(r) { await supabase.from('recurring_invoices').update({ active:!r.active }).eq('id',r.id); load() }
  async function deleteRec(id) { if(!window.confirm('Delete?')) return; await supabase.from('recurring_invoices').delete().eq('id',id); load() }

  // ── payment plan helpers ──
  function buildInstallments(total,num,start) {
    const amt = parseFloat(total)/num
    return Array.from({length:num},(_,i)=>{ const d=new Date(start); d.setMonth(d.getMonth()+i); return { number:i+1, amount:amt.toFixed(2), due_date:d.toISOString().split('T')[0], status:'pending' } })
  }

  const planPreview = planForm.total_amount && planForm.num_installments ? buildInstallments(planForm.total_amount, planForm.num_installments, planForm.start_date) : []

  async function savePlan() {
    if (!planForm.client_id || !planForm.total_amount) { notify('Fill required fields'); return }
    await supabase.from('payment_plans').insert({ client_id:planForm.client_id, invoice_id:planForm.invoice_id||null, total_amount:parseFloat(planForm.total_amount), installments:buildInstallments(planForm.total_amount,planForm.num_installments,planForm.start_date), status:'active' })
    notify('Payment plan created!')
    setShowPlanModal(false)
    setPlanForm({ client_id:'', invoice_id:'', total_amount:'', num_installments:3, start_date:new Date().toISOString().split('T')[0] })
    load()
  }

  async function markInstPaid(plan, i) {
    const updated = plan.installments.map((inst,j)=>j===i?{...inst,status:'paid',paid_at:new Date().toISOString()}:inst)
    await supabase.from('payment_plans').update({ installments:updated, status:updated.every(x=>x.status==='paid')?'completed':'active' }).eq('id',plan.id)
    notify('Installment marked paid!')
    load()
  }

  async function deletePlan(id) { if(!window.confirm('Delete?')) return; await supabase.from('payment_plans').delete().eq('id',id); load() }

  // ── overview stats ──
  const totalInvoiced   = invoices.reduce((s,i)=>s+(i.total_amount||0),0)
  const totalCollected  = invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+(i.total_amount||0),0)
  const totalOutstanding= invoices.filter(i=>['sent','overdue'].includes(i.status)).reduce((s,i)=>s+(i.total_amount||0),0)
  const totalOverdue    = invoices.filter(i=>i.status==='overdue').reduce((s,i)=>s+(i.total_amount||0),0)
  const monthlyMRR      = recurring.filter(r=>r.active&&r.frequency==='monthly').reduce((s,r)=>s+r.line_items.reduce((ss,l)=>ss+(l.qty*parseFloat(l.rate||0)),0),0)
  const planOutstanding = plans.filter(p=>p.status==='active').reduce((s,p)=>s+p.installments.filter(i=>i.status==='pending').reduce((ss,i)=>ss+parseFloat(i.amount),0),0)

  const TABS = [
    { id:'overview', label:'Overview', icon:'📊' },
    { id:'invoices', label:'Invoices', icon:'🧾', badge: invoices.filter(i=>i.status==='overdue').length || null },
    { id:'recurring', label:'Recurring', icon:'🔄', badge: recurring.filter(r=>r.active&&new Date(r.next_invoice_date)<=new Date()).length || null },
    { id:'plans', label:'Payment Plans', icon:'📋', badge: plans.filter(p=>p.status==='active').length || null },
  ]

  if (loading) return <div>{[...Array(4)].map((_,i)=><div key={i} className="skeleton" style={{height:80,marginBottom:12}} />)}</div>

  return (
    <div>
      {toast && <div style={{position:'fixed',bottom:24,right:24,background:'#202326',color:'#CBB17B',padding:'12px 20px',borderRadius:10,fontSize:13,fontWeight:700,zIndex:9999,boxShadow:'0 8px 24px rgba(0,0,0,0.2)'}}>{toast}</div>}

      {/* Header */}
      <div className="card" style={{padding:'20px 24px',marginBottom:20,background:'#202326',border:'none'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <h2 style={{color:'#fff',fontSize:20,marginBottom:4}}>Billing Hub</h2>
            <p style={{color:'rgba(255,255,255,0.6)',fontSize:13,margin:0}}>Invoices, recurring billing, and payment plans — all in one place.</p>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button className="btn btn-ghost" style={{color:'#CBB17B',border:'1px solid rgba(201,168,76,0.3)',fontSize:12}} onClick={()=>{setShowPlanModal(true);setTab('plans')}}>+ Payment Plan</button>
            <button className="btn btn-ghost" style={{color:'#CBB17B',border:'1px solid rgba(201,168,76,0.3)',fontSize:12}} onClick={()=>{openNewRec();setTab('recurring')}}>+ Recurring</button>
            <button className="btn btn-primary" style={{fontSize:13}} onClick={()=>{setShowInvModal(true);setTab('invoices')}}>+ New Invoice</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:20,background:'#F7F3EB',padding:4,borderRadius:12,width:'fit-content'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:'9px 18px',borderRadius:9,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,background:tab===t.id?'#fff':'transparent',color:tab===t.id?'#202326':'#62615D',boxShadow:tab===t.id?'0 2px 8px rgba(0,0,0,0.08)':'none',transition:'all 0.15s',display:'flex',alignItems:'center',gap:6}}>
            {t.icon} {t.label}
            {t.badge ? <span style={{background:'#EF4444',color:'#fff',fontSize:10,fontWeight:800,padding:'1px 6px',borderRadius:20,marginLeft:2}}>{t.badge}</span> : null}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab==='overview' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
            {[
              {label:'Total Invoiced',   value:fmt(totalInvoiced),    sub:`${invoices.length} invoices`,          color:'#CBB17B', icon:'🧾'},
              {label:'Collected',        value:fmt(totalCollected),   sub:`${Math.round((totalCollected/Math.max(totalInvoiced,1))*100)}% collection rate`, color:'#10B981', icon:'💰'},
              {label:'Outstanding',      value:fmt(totalOutstanding), sub:totalOverdue>0?`${fmt(totalOverdue)} overdue`:'None overdue', color:totalOverdue>0?'#EF4444':'#F59E0B', icon:'⏳'},
              {label:'Monthly MRR',      value:fmt(monthlyMRR),       sub:`${recurring.filter(r=>r.active).length} active schedules`, color:'#8B5CF6', icon:'🔄'},
            ].map(s=>(
              <div key={s.label} className="card" style={{padding:'18px 20px',position:'relative',overflow:'hidden'}}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:s.color}} />
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontSize:11,color:'#62615D',fontWeight:600,textTransform:'uppercase',letterSpacing:0.5,marginBottom:6}}>{s.label}</div>
                    <div style={{fontSize:22,fontWeight:800,color:'#202326',fontFamily:"'DM Sans',sans-serif"}}>{s.value}</div>
                    <div style={{fontSize:11,color:'#62615D',marginTop:4}}>{s.sub}</div>
                  </div>
                  <div style={{fontSize:24,opacity:0.7}}>{s.icon}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent activity */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            {/* Recent invoices */}
            <div className="card" style={{padding:0,overflow:'hidden'}}>
              <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:14,fontWeight:700}}>Recent Invoices</span>
                <button onClick={()=>setTab('invoices')} style={{fontSize:12,color:'#CBB17B',background:'none',border:'none',cursor:'pointer'}}>View all →</button>
              </div>
              {invoices.slice(0,5).map((inv,i)=>(
                <div key={inv.id} style={{padding:'12px 18px',display:'flex',alignItems:'center',gap:10,borderBottom:i<4?'1px solid var(--border)':'none'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#202326'}}>{clientName(inv.client_id)}</div>
                    <div style={{fontSize:11,color:'#62615D'}}>{inv.invoice_number}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:13,fontWeight:700}}>{fmt(inv.total_amount)}</div>
                    <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,background:STATUS_BG[inv.status],color:STATUS_COLORS[inv.status]}}>{inv.status}</span>
                  </div>
                </div>
              ))}
              {invoices.length===0 && <div style={{padding:24,textAlign:'center',color:'#62615D',fontSize:13}}>No invoices yet</div>}
            </div>

            {/* Upcoming recurring */}
            <div className="card" style={{padding:0,overflow:'hidden'}}>
              <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:14,fontWeight:700}}>Upcoming Recurring</span>
                <button onClick={()=>setTab('recurring')} style={{fontSize:12,color:'#CBB17B',background:'none',border:'none',cursor:'pointer'}}>View all →</button>
              </div>
              {recurring.filter(r=>r.active).slice(0,5).map((r,i)=>{
                const isDue = new Date(r.next_invoice_date)<=new Date()
                const sub = r.line_items.reduce((s,l)=>s+(l.qty*parseFloat(l.rate||0)),0)
                return (
                  <div key={r.id} style={{padding:'12px 18px',display:'flex',alignItems:'center',gap:10,borderBottom:i<4?'1px solid var(--border)':'none'}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#202326'}}>{clientName(r.client_id)}</div>
                      <div style={{fontSize:11,color:isDue?'#EF4444':'#62615D',fontWeight:isDue?700:400}}>{isDue?'⚠️ Due now':`Next: ${new Date(r.next_invoice_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:13,fontWeight:700}}>{fmt(sub)}</div>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,background:FREQ_COLORS[r.frequency]+'20',color:FREQ_COLORS[r.frequency]}}>{FREQ_LABELS[r.frequency]}</span>
                    </div>
                  </div>
                )
              })}
              {recurring.filter(r=>r.active).length===0 && <div style={{padding:24,textAlign:'center',color:'#62615D',fontSize:13}}>No recurring schedules</div>}
            </div>

            {/* Payment plans summary */}
            {plans.filter(p=>p.status==='active').length>0 && (
              <div className="card" style={{padding:0,overflow:'hidden',gridColumn:'1/-1'}}>
                <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:14,fontWeight:700}}>Active Payment Plans — {fmt(planOutstanding)} outstanding</span>
                  <button onClick={()=>setTab('plans')} style={{fontSize:12,color:'#CBB17B',background:'none',border:'none',cursor:'pointer'}}>View all →</button>
                </div>
                {plans.filter(p=>p.status==='active').slice(0,3).map((p,i)=>{
                  const paid = p.installments.filter(x=>x.status==='paid').reduce((s,x)=>s+parseFloat(x.amount),0)
                  const pct = (paid/p.total_amount)*100
                  return (
                    <div key={p.id} style={{padding:'12px 18px',borderBottom:i<2?'1px solid var(--border)':'none'}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                        <span style={{fontSize:13,fontWeight:600,color:'#202326'}}>{clientName(p.client_id)}</span>
                        <span style={{fontSize:13,fontWeight:700}}>{fmt(p.total_amount)} · {p.installments.length} installments</span>
                      </div>
                      <div style={{height:6,background:'#F0EBE0',borderRadius:3,overflow:'hidden'}}>
                        <div style={{height:'100%',background:'#10B981',width:`${pct}%`,borderRadius:3,transition:'width 0.5s'}} />
                      </div>
                      <div style={{fontSize:11,color:'#62615D',marginTop:4}}>{fmt(paid)} paid · {fmt(p.total_amount-paid)} remaining</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── INVOICES TAB ── */}
      {tab==='invoices' && (
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
            <button className="btn btn-primary" onClick={()=>setShowInvModal(true)}>+ New Invoice</button>
          </div>
          <div className="card" style={{padding:0,overflow:'hidden'}}>
            <div style={{padding:'12px 18px',background:'#F7F3EB',borderBottom:'1px solid var(--border)',display:'grid',gridTemplateColumns:'1fr 120px 100px 100px 80px',gap:8,fontSize:11,fontWeight:700,color:'#62615D',textTransform:'uppercase',letterSpacing:0.5}}>
              <span>Client</span><span>Invoice #</span><span>Amount</span><span>Due</span><span>Status</span>
            </div>
            {invoices.length===0 ? <div style={{padding:40,textAlign:'center',color:'#62615D',fontSize:13}}>No invoices yet</div>
            : invoices.map((inv,i)=>(
              <div id={inv.id} key={inv.id} style={{padding:'13px 18px',display:'grid',gridTemplateColumns:'1fr 120px 100px 100px 80px',gap:8,alignItems:'center',borderBottom:i<invoices.length-1?'1px solid var(--border)':'none',background:inv.id===highlightId?'rgba(201, 168, 76, 0.12)':'',transition:'background 0.3s',borderLeft:inv.id===highlightId?'3px solid #CBB17B':'3px solid transparent'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:'#202326'}}>{clientName(inv.client_id)}</div>
                  {inv.notes && <div style={{fontSize:11,color:'#62615D',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:200}}>{inv.notes}</div>}
                </div>
                <div style={{fontSize:12,color:'#CBB17B',cursor:'pointer',textDecoration:'underline'}} onClick={()=>{setViewingInv(inv);setEditMode(false);setEditForm({...inv,line_items:inv.line_items||[{description:'',qty:1,rate:''}]});setShowViewModal(true)}}>{inv.invoice_number}</div>
                <div style={{fontSize:13,fontWeight:700,color:'#202326'}}>{fmt(inv.total_amount)}</div>
                <div style={{fontSize:12,color:'#62615D'}}>{inv.due_date?new Date(inv.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric'}):'—'}</div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:20,background:STATUS_BG[inv.status],color:STATUS_COLORS[inv.status]}}>{inv.status}</span>
                  {inv.status!=='paid' && <button onClick={()=>updateInvoiceStatus(inv.id,'paid')} style={{background:'none',border:'none',cursor:'pointer',color:'#10B981',fontSize:12}} title="Mark paid">✓</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── RECURRING TAB ── */}
      {tab==='recurring' && (
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
            <button className="btn btn-primary" onClick={openNewRec}>+ New Recurring Invoice</button>
          </div>
          <div className="card" style={{padding:0,overflow:'hidden'}}>
            {recurring.length===0 ? <div style={{padding:40,textAlign:'center',color:'#62615D',fontSize:13}}>No recurring invoices. Create one to automate billing!</div>
            : recurring.map((r,i)=>{
              const sub = r.line_items.reduce((s,l)=>s+(l.qty*parseFloat(l.rate||0)),0)
              const isDue = new Date(r.next_invoice_date)<=new Date()
              return (
                <div key={r.id} style={{padding:'14px 18px',display:'flex',alignItems:'center',gap:12,borderBottom:i<recurring.length-1?'1px solid var(--border)':'none',opacity:r.active?1:0.55}}>
                  <div style={{width:36,height:36,borderRadius:9,background:FREQ_COLORS[r.frequency]+'20',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>🔄</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#202326'}}>{clientName(r.client_id)}</div>
                    <div style={{fontSize:11,color:'#62615D'}}>{r.line_items.map(l=>l.description).filter(Boolean).join(', ')}</div>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,padding:'3px 9px',borderRadius:20,background:FREQ_COLORS[r.frequency]+'20',color:FREQ_COLORS[r.frequency]}}>{FREQ_LABELS[r.frequency]}</span>
                  <div style={{textAlign:'right',minWidth:90}}>
                    <div style={{fontSize:14,fontWeight:800,color:'#202326'}}>{fmt(sub)}</div>
                    <div style={{fontSize:11,color:isDue?'#EF4444':'#62615D',fontWeight:isDue?700:400}}>{isDue?'⚠️ Due now':`Next: ${new Date(r.next_invoice_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`}</div>
                  </div>
                  <div style={{display:'flex',gap:6,flexShrink:0}}>
                    <button onClick={()=>generateNow(r)} disabled={!r.active||genLoading===r.id} className="btn btn-primary" style={{fontSize:11,padding:'6px 11px'}}>{genLoading===r.id?'...':'⚡ Generate'}</button>
                    <button onClick={()=>openEditRec(r)} className="btn btn-ghost" style={{fontSize:11,padding:'6px 10px'}}>Edit</button>
                    <button onClick={()=>toggleRec(r)} className="btn btn-ghost" style={{fontSize:11,padding:'6px 10px',color:r.active?'#F59E0B':'#10B981'}}>{r.active?'Pause':'Resume'}</button>
                    <button onClick={()=>deleteRec(r.id)} className="btn btn-ghost" style={{fontSize:11,padding:'6px 8px',color:'#EF4444'}}>✕</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── PAYMENT PLANS TAB ── */}
      {tab==='plans' && (
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:14}}>
            <button className="btn btn-primary" onClick={()=>setShowPlanModal(true)}>+ New Payment Plan</button>
          </div>
          {plans.length===0 ? <div className="card" style={{padding:40,textAlign:'center',color:'#62615D',fontSize:13}}>No payment plans yet.</div>
          : plans.map(plan=>{
            const paid = plan.installments.filter(i=>i.status==='paid').reduce((s,i)=>s+parseFloat(i.amount),0)
            const pct  = (paid/plan.total_amount)*100
            return (
              <div key={plan.id} className="card" style={{padding:0,overflow:'hidden',marginBottom:14}}>
                <div style={{padding:'14px 18px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid var(--border)',background:'#F7F3EB'}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:'#202326'}}>{clientName(plan.client_id)}</div>
                    <div style={{fontSize:12,color:'#62615D',marginTop:2}}>{fmt(plan.total_amount)} total · {plan.installments.length} installments</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:plan.status==='completed'?'#D1FAE5':'#DBEAFE',color:plan.status==='completed'?'#065F46':'#1E40AF'}}>{plan.status==='completed'?'✅ Completed':'🔄 Active'}</span>
                    <button onClick={()=>deletePlan(plan.id)} className="btn btn-ghost" style={{fontSize:11,color:'#EF4444',padding:'5px 9px'}}>✕</button>
                  </div>
                </div>
                <div style={{padding:'10px 18px',borderBottom:'1px solid var(--border)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#62615D',marginBottom:5}}>
                    <span>Paid: <strong style={{color:'#10B981'}}>{fmt(paid)}</strong></span>
                    <span>Remaining: <strong style={{color:'#F59E0B'}}>{fmt(plan.total_amount-paid)}</strong></span>
                  </div>
                  <div style={{height:7,background:'#F0EBE0',borderRadius:4,overflow:'hidden'}}>
                    <div style={{height:'100%',background:'#10B981',width:`${pct}%`,borderRadius:4,transition:'width 0.5s'}} />
                  </div>
                </div>
                {plan.installments.map((inst,i)=>(
                  <div key={i} style={{padding:'11px 18px',display:'flex',alignItems:'center',gap:12,borderBottom:i<plan.installments.length-1?'1px solid var(--border)':'none'}}>
                    <div style={{width:26,height:26,borderRadius:'50%',background:inst.status==='paid'?'#D1FAE5':'#F7F3EB',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:inst.status==='paid'?'#065F46':'#62615D',flexShrink:0}}>{inst.status==='paid'?'✓':inst.number}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#202326'}}>Installment {inst.number} — {fmt(inst.amount)}</div>
                      <div style={{fontSize:11,color:'#62615D'}}>Due: {new Date(inst.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
                    </div>
                    {inst.status==='paid'
                      ? <span style={{fontSize:12,color:'#10B981',fontWeight:700}}>✅ Paid</span>
                      : plan.status!=='completed' && <button onClick={()=>markInstPaid(plan,i)} className="btn btn-ghost" style={{fontSize:11,padding:'5px 11px',color:'#10B981',border:'1px solid #10B981'}}>Mark Paid</button>}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {/* ── INVOICE MODAL ── */}
      {showInvModal && (
        <Modal title="New Invoice" onClose={()=>setShowInvModal(false)}>
          <div className="form-group">
            <label className="label">Client *</label>
            <select className="input" value={invForm.client_id} onChange={e=>setInvForm(f=>({...f,client_id:e.target.value}))}>
              <option value="">Select client...</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.business_name||c.full_name}</option>)}
            </select>
          </div>
          <label className="label" style={{marginBottom:8,display:'block'}}>Line Items</label>
          {invForm.line_items.map((line,i)=>(
            <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
              <input className="input" style={{flex:3}} placeholder="Description" value={line.description} onChange={e=>setInvLine(i,'description',e.target.value)} />
              <input className="input" style={{width:56}} type="number" placeholder="Qty" value={line.qty} onChange={e=>setInvLine(i,'qty',e.target.value)} min="1" />
              <input className="input" style={{width:86}} type="number" placeholder="Rate $" value={line.rate} onChange={e=>setInvLine(i,'rate',e.target.value)} />
              {invForm.line_items.length>1 && <button onClick={()=>setInvForm(f=>({...f,line_items:f.line_items.filter((_,j)=>j!==i)}))} style={{background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:18}}>×</button>}
            </div>
          ))}
          <button className="btn btn-ghost" style={{fontSize:12,marginBottom:14}} onClick={()=>setInvForm(f=>({...f,line_items:[...f.line_items,{description:'',qty:1,rate:''}]}))}>+ Add Line</button>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
            <div className="form-group"><label className="label">Due Date</label><input className="input" type="date" value={invForm.due_date} onChange={e=>setInvForm(f=>({...f,due_date:e.target.value}))} /></div>
            <div className="form-group"><label className="label">Tax Rate %</label><input className="input" type="number" placeholder="0" value={invForm.tax_rate} onChange={e=>setInvForm(f=>({...f,tax_rate:e.target.value}))} /></div>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',background:'#F7F3EB',padding:'10px 14px',borderRadius:8,marginBottom:14}}>
            <span style={{fontSize:13,fontWeight:700}}>Total</span>
            <span style={{fontSize:16,fontWeight:800,color:'#202326'}}>{fmt(invTotal*(1+(invForm.tax_rate||0)/100))}</span>
          </div>
          <div className="form-group"><label className="label">Notes</label><textarea className="input" rows={2} value={invForm.notes} onChange={e=>setInvForm(f=>({...f,notes:e.target.value}))} placeholder="Optional..." /></div>
          <div style={{display:'flex',gap:10,marginTop:8}}>
            <button className="btn btn-ghost" onClick={()=>setShowInvModal(false)} style={{flex:1,justifyContent:'center'}}>Cancel</button>
            <button className="btn btn-primary" onClick={saveInvoice} disabled={invGenerating} style={{flex:2,justifyContent:'center'}}>{invGenerating?'Creating...':'Create Invoice'}</button>
          </div>
        </Modal>
      )}

      {/* ── RECURRING MODAL ── */}
      {showRecModal && (
        <Modal title={editingRec?'Edit Recurring Invoice':'New Recurring Invoice'} onClose={()=>setShowRecModal(false)}>
          <div className="form-group"><label className="label">Client *</label>
            <select className="input" value={recForm.client_id} onChange={e=>setRecForm(f=>({...f,client_id:e.target.value}))}>
              <option value="">Select client...</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.business_name||c.full_name}</option>)}
            </select>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group"><label className="label">Frequency</label>
              <select className="input" value={recForm.frequency} onChange={e=>setRecForm(f=>({...f,frequency:e.target.value,next_invoice_date:nextDate(e.target.value)}))}>
                <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annually">Annually</option>
              </select>
            </div>
            <div className="form-group"><label className="label">Next Invoice Date</label><input className="input" type="date" value={recForm.next_invoice_date} onChange={e=>setRecForm(f=>({...f,next_invoice_date:e.target.value}))} /></div>
          </div>
          <label className="label" style={{marginBottom:8,display:'block'}}>Line Items</label>
          {recForm.line_items.map((line,i)=>(
            <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
              <input className="input" style={{flex:3}} placeholder="Description" value={line.description} onChange={e=>setRecLine(i,'description',e.target.value)} />
              <input className="input" style={{width:56}} type="number" placeholder="Qty" value={line.qty} onChange={e=>setRecLine(i,'qty',e.target.value)} min="1" />
              <input className="input" style={{width:86}} type="number" placeholder="Rate $" value={line.rate} onChange={e=>setRecLine(i,'rate',e.target.value)} />
              {recForm.line_items.length>1 && <button onClick={()=>setRecForm(f=>({...f,line_items:f.line_items.filter((_,j)=>j!==i)}))} style={{background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:18}}>×</button>}
            </div>
          ))}
          <button className="btn btn-ghost" style={{fontSize:12,marginBottom:14}} onClick={()=>setRecForm(f=>({...f,line_items:[...f.line_items,{description:'',qty:1,rate:''}]}))}>+ Add Line</button>
          <div style={{display:'flex',justifyContent:'space-between',background:'#F7F3EB',padding:'10px 14px',borderRadius:8,marginBottom:14}}>
            <span style={{fontSize:13,fontWeight:700}}>Per Invoice</span>
            <span style={{fontSize:16,fontWeight:800,color:'#202326'}}>{fmt(recTotal)}</span>
          </div>
          <div className="form-group"><label className="label">Notes</label><textarea className="input" rows={2} value={recForm.notes} onChange={e=>setRecForm(f=>({...f,notes:e.target.value}))} /></div>
          <div style={{display:'flex',gap:10,marginTop:8}}>
            <button className="btn btn-ghost" onClick={()=>setShowRecModal(false)} style={{flex:1,justifyContent:'center'}}>Cancel</button>
            <button className="btn btn-primary" onClick={saveRec} style={{flex:2,justifyContent:'center'}}>{editingRec?'Save Changes':'Create Recurring'}</button>
          </div>
        </Modal>
      )}

      {/* ── PAYMENT PLAN MODAL ── */}
      {showPlanModal && (
        <Modal title="New Payment Plan" onClose={()=>setShowPlanModal(false)}>
          <div className="form-group"><label className="label">Client *</label>
            <select className="input" value={planForm.client_id} onChange={e=>setPlanForm(f=>({...f,client_id:e.target.value,invoice_id:''}))}>
              <option value="">Select client...</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.business_name||c.full_name}</option>)}
            </select>
          </div>
          {planForm.client_id && invoices.filter(i=>i.client_id===planForm.client_id&&['sent','overdue'].includes(i.status)).length>0 && (
            <div className="form-group"><label className="label">Link to Invoice (optional)</label>
              <select className="input" value={planForm.invoice_id} onChange={e=>{ const inv=invoices.find(i=>i.id===e.target.value); setPlanForm(f=>({...f,invoice_id:e.target.value,total_amount:inv?inv.total_amount:f.total_amount})) }}>
                <option value="">No invoice linked</option>
                {invoices.filter(i=>i.client_id===planForm.client_id&&['sent','overdue'].includes(i.status)).map(inv=><option key={inv.id} value={inv.id}>{inv.invoice_number} — {fmt(inv.total_amount)}</option>)}
              </select>
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group"><label className="label">Total Amount *</label><input className="input" type="number" placeholder="0.00" value={planForm.total_amount} onChange={e=>setPlanForm(f=>({...f,total_amount:e.target.value}))} /></div>
            <div className="form-group"><label className="label">Installments</label>
              <select className="input" value={planForm.num_installments} onChange={e=>setPlanForm(f=>({...f,num_installments:parseInt(e.target.value)}))}>
                {[2,3,4,5,6,12].map(n=><option key={n} value={n}>{n} payments</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="label">First Payment Date</label><input className="input" type="date" value={planForm.start_date} onChange={e=>setPlanForm(f=>({...f,start_date:e.target.value}))} /></div>
          {planPreview.length>0 && (
            <div style={{background:'#F7F3EB',borderRadius:10,padding:'12px 16px',marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:'#202326',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>Payment Schedule</div>
              {planPreview.map((inst,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'4px 0',borderBottom:i<planPreview.length-1?'1px solid #DED6CA':'none'}}>
                  <span style={{color:'#62615D'}}>#{inst.number} · {new Date(inst.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</span>
                  <span style={{fontWeight:700,color:'#202326'}}>{fmt(inst.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{display:'flex',gap:10,marginTop:8}}>
            <button className="btn btn-ghost" onClick={()=>setShowPlanModal(false)} style={{flex:1,justifyContent:'center'}}>Cancel</button>
            <button className="btn btn-primary" onClick={savePlan} style={{flex:2,justifyContent:'center'}}>Create Payment Plan</button>
          </div>
        </Modal>
      )}

      {showViewModal && viewingInv && (
        <Modal title={editMode ? `Edit ${viewingInv.invoice_number}` : `Invoice ${viewingInv.invoice_number}`} onClose={()=>{setShowViewModal(false);setEditMode(false)}} wide>
          {!editMode ? (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
                <div>
                  <div style={{fontSize:22,fontWeight:800,color:'#202326',fontFamily:"'DM Sans',sans-serif"}}>{clientName(viewingInv.client_id)}</div>
                  <div style={{fontSize:13,color:'#62615D',marginTop:4}}>Invoice {viewingInv.invoice_number} · {viewingInv.due_date ? `Due ${new Date(viewingInv.due_date).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}` : 'No due date'}</div>
                </div>
                <span style={{fontSize:12,fontWeight:700,padding:'5px 14px',borderRadius:20,background:STATUS_BG[viewingInv.status],color:STATUS_COLORS[viewingInv.status]}}>{viewingInv.status}</span>
              </div>

              <div style={{background:'#F7F3EB',borderRadius:10,padding:'16px',marginBottom:16}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 90px',gap:8,fontSize:11,fontWeight:700,color:'#62615D',textTransform:'uppercase',letterSpacing:0.5,marginBottom:10}}>
                  <span>Description</span><span>Qty</span><span>Rate</span><span style={{textAlign:'right'}}>Amount</span>
                </div>
                {(viewingInv.line_items||[]).map((l,i)=>(
                  <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 90px',gap:8,padding:'8px 0',borderTop:'1px solid #DED6CA',fontSize:13}}>
                    <span style={{color:'#202326'}}>{l.description}</span>
                    <span style={{color:'#62615D'}}>{l.qty}</span>
                    <span style={{color:'#62615D'}}>{fmt(l.rate)}</span>
                    <span style={{textAlign:'right',fontWeight:600,color:'#202326'}}>{fmt(l.qty*parseFloat(l.rate||0))}</span>
                  </div>
                ))}
                <div style={{borderTop:'2px solid #CBB17B',marginTop:10,paddingTop:10,display:'flex',justifyContent:'space-between'}}>
                  <span style={{fontSize:13,fontWeight:700,color:'#202326'}}>Total</span>
                  <span style={{fontSize:18,fontWeight:800,color:'#202326',fontFamily:"'DM Sans',sans-serif"}}>{fmt(viewingInv.total_amount)}</span>
                </div>
              </div>

              {viewingInv.notes && <div style={{background:'#F7F3EB',borderRadius:8,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#62615D'}}><strong style={{color:'#202326'}}>Notes:</strong> {viewingInv.notes}</div>}

              <div style={{display:'flex',gap:10,marginTop:8}}>
                <button className="btn btn-ghost" onClick={()=>setShowViewModal(false)} style={{flex:1,justifyContent:'center'}}>Close</button>
                {viewingInv.status !== 'paid' && <button className="btn btn-ghost" onClick={()=>updateInvoiceStatus(viewingInv.id,'paid').then(()=>{setShowViewModal(false)})} style={{flex:1,justifyContent:'center',color:'#10B981',border:'1px solid #10B981'}}>✓ Mark Paid</button>}
                <button className="btn btn-primary" onClick={()=>setEditMode(true)} style={{flex:1,justifyContent:'center'}}>✏️ Edit</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="form-group">
                <label className="label">Client</label>
                <select className="input" value={editForm.client_id} onChange={e=>setEditForm(f=>({...f,client_id:e.target.value}))}>
                  {clients.map(c=><option key={c.id} value={c.id}>{c.business_name||c.full_name}</option>)}
                </select>
              </div>
              <label className="label" style={{marginBottom:8,display:'block'}}>Line Items</label>
              {editForm.line_items.map((line,i)=>(
                <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                  <input className="input" style={{flex:3}} placeholder="Description" value={line.description} onChange={e=>setEditForm(f=>({...f,line_items:f.line_items.map((l,j)=>j===i?{...l,description:e.target.value}:l)}))} />
                  <input className="input" style={{width:56}} type="number" placeholder="Qty" value={line.qty} onChange={e=>setEditForm(f=>({...f,line_items:f.line_items.map((l,j)=>j===i?{...l,qty:e.target.value}:l)}))} />
                  <input className="input" style={{width:86}} type="number" placeholder="Rate $" value={line.rate} onChange={e=>setEditForm(f=>({...f,line_items:f.line_items.map((l,j)=>j===i?{...l,rate:e.target.value}:l)}))} />
                  {editForm.line_items.length>1 && <button onClick={()=>setEditForm(f=>({...f,line_items:f.line_items.filter((_,j)=>j!==i)}))} style={{background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:18}}>×</button>}
                </div>
              ))}
              <button className="btn btn-ghost" style={{fontSize:12,marginBottom:14}} onClick={()=>setEditForm(f=>({...f,line_items:[...f.line_items,{description:'',qty:1,rate:''}]}))}>+ Add Line</button>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                <div className="form-group"><label className="label">Due Date</label><input className="input" type="date" value={editForm.due_date||''} onChange={e=>setEditForm(f=>({...f,due_date:e.target.value}))} /></div>
                <div className="form-group"><label className="label">Status</label>
                  <select className="input" value={editForm.status} onChange={e=>setEditForm(f=>({...f,status:e.target.value}))}>
                    <option value="draft">Draft</option><option value="sent">Sent</option><option value="paid">Paid</option><option value="overdue">Overdue</option><option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group"><label className="label">Tax Rate %</label><input className="input" type="number" placeholder="0" value={editForm.tax_rate||0} onChange={e=>setEditForm(f=>({...f,tax_rate:e.target.value}))} /></div>
              </div>
              <div className="form-group"><label className="label">Notes</label><textarea className="input" rows={2} value={editForm.notes||''} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} /></div>
              <div style={{display:'flex',gap:10,marginTop:8}}>
                <button className="btn btn-ghost" onClick={()=>setEditMode(false)} style={{flex:1,justifyContent:'center'}}>Cancel</button>
                <button className="btn btn-primary" onClick={async()=>{
                  const sub = editForm.line_items.reduce((s,l)=>s+(l.qty*parseFloat(l.rate||0)),0)
                  const tax = sub*((editForm.tax_rate||0)/100)
                  await supabase.from('invoices').update({
                    client_id:editForm.client_id,
                    line_items:editForm.line_items.filter(l=>l.description),
                    tax_rate:editForm.tax_rate,
                    total_amount:sub+tax,
                    status:editForm.status,
                    notes:editForm.notes,
                    due_date:editForm.due_date||null,
                    updated_at:new Date().toISOString()
                  }).eq('id',viewingInv.id)
                  notify('Invoice updated!')
                  setShowViewModal(false)
                  setEditMode(false)
                  load()
                }} style={{flex:2,justifyContent:'center'}}>Save Changes</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}
